(() => {
  const script = document.currentScript || document.querySelector('[data-sc-upcart-pot-upsell-script]');
  const stylesheetUrl = script?.dataset.stylesheetUrl || '';
  const productView = script?.dataset.productView || 'sc-upcart-pot-upsell';
  const widgetSelector = '#sc-upcart-pot-upsell';
  const injectedWidgetSelector = '[data-sc-upcart-pot-upsell-injected]';
  const productTileSelector = '.upcart-internal-component-product-tile';
  const legacyProductRowSelector = '.upcart-product-item';
  const productLinkSelector = '.upcart-internal-component-product-tile__title-link';
  const maxSubscriptionAttempts = 40;
  const markupCache = new Map();
  let renderToken = 0;

  class ScUpcartPotUpsell extends HTMLElement {
    constructor() {
      super();

      this.onClick = this.onClick.bind(this);
      this.syncVisibility = this.syncVisibility.bind(this);
      this.visibilityObserver = null;
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.addEventListener('click', this.onClick);

      if (typeof ResizeObserver === 'function') {
        this.visibilityObserver = new ResizeObserver(this.syncVisibility);
        this.visibilityObserver.observe(this);
      }

      window.requestAnimationFrame(this.syncVisibility);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.visibilityObserver?.disconnect();
      this.visibilityObserver = null;
      delete this.dataset.initialized;
    }

    syncVisibility() {
      this.dataset.visible = String(window.getComputedStyle(this).display !== 'none');
    }

    onClick(event) {
      const swatch = event.target.closest('[data-color-dot]');

      if (swatch && this.contains(swatch)) {
        event.preventDefault();
        event.stopPropagation();

        if (!swatch.disabled) this.selectVariant(swatch);
        return;
      }

      const addButton = event.target.closest('[data-upsell-add]');

      if (addButton && this.contains(addButton)) {
        event.preventDefault();
        event.stopPropagation();

        this.addToCart(addButton);
        return;
      }

      const item = event.target.closest('[data-upsell-item]');
      const productLink = item?.querySelector('[data-product-link]');

      if (item && this.contains(item) && productLink && !event.target.closest('[data-product-link]')) {
        window.location.assign(productLink.href);
      }
    }

    selectVariant(swatch) {
      const item = swatch.closest('[data-upsell-item]');

      if (!item) return;

      item.dataset.variantId = swatch.dataset.variantId || '';

      item.querySelectorAll('[data-color-dot]').forEach((dot) => {
        dot.classList.toggle('is-active', dot === swatch);
      });

      const image = item.querySelector('[data-upsell-image]');

      if (image && swatch.dataset.image) image.src = swatch.dataset.image;

      const currentPrice = item.querySelector('[data-current-price]');
      const comparePrice = item.querySelector('[data-compare-price]');
      const priceContainer = item.querySelector('[data-price-container]');

      if (currentPrice) currentPrice.textContent = `+ ${swatch.dataset.price || ''}`;

      if (swatch.dataset.compareAtPrice) {
        if (comparePrice) {
          comparePrice.textContent = `+ ${swatch.dataset.compareAtPrice}`;
        } else if (priceContainer) {
          const newComparePrice = document.createElement('s');
          newComparePrice.className = 'sc-upcart-pot-upsell-item__compare-price';
          newComparePrice.dataset.comparePrice = '';
          newComparePrice.textContent = `+ ${swatch.dataset.compareAtPrice}`;
          priceContainer.prepend(newComparePrice);
        }
      } else if (comparePrice) {
        comparePrice.remove();
      }

      if (item.dataset.productUrl) {
        const url = new URL(item.dataset.productUrl, window.location.origin);
        url.searchParams.set('variant', item.dataset.variantId);

        item.querySelectorAll('[data-product-link]').forEach((productLink) => {
          productLink.href = `${url.pathname}${url.search}`;
        });
      }
    }

    getParentLineKey() {
      const nativeParent = this.closest('[data-sc-cart-key]');

      if (nativeParent?.dataset.scCartKey) return nativeParent.dataset.scCartKey;

      const upcartTile =
        this.closest('.upcart-internal-component-product-tile') ||
        this.closest('.upcart-product-item');

      if (upcartTile?.id) return upcartTile.id;

      return this.dataset.parentLineKey || null;
    }

    async addToCart(button) {
      if (button.disabled) return;

      const item = button.closest('[data-upsell-item]');
      const variantId = Number(item?.dataset.variantId);

      if (!variantId) return;

      const errorElement = this.querySelector('[data-upsell-error]');
      const spinner = button.querySelector('[data-upsell-spinner]');
      const parentLineKey = this.getParentLineKey();

      if (errorElement) {
        errorElement.hidden = true;
        errorElement.textContent = '';
      }

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (spinner) spinner.hidden = false;

      try {
        const shopifyRoot = window.Shopify?.routes?.root || '/';
        const cartSections = this.getCartSections();
        const lineItem = {
          id: variantId,
          quantity: 1,
        };

        // Nest the pot under the plant so checkout shows the parent/child relationship.
        if (parentLineKey) lineItem.parent_line_key = parentLineKey;

        const requestBody = {
          items: [lineItem],
        };

        if (cartSections.length) {
          requestBody.sections = cartSections.map((section) => section.section);
          requestBody.sections_url = window.location.pathname;
        }

        const response = await fetch(`${shopifyRoot}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });
        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Unable to add this product to the cart.');
        }

        // Hide the upsell immediately so it does not linger until a full reload.
        if (parentLineKey) {
          this.closest('[data-sc-cart-key]')
            ?.querySelector(':scope > .sc-cart-drawer__pot-upsell')
            ?.remove();
        }

        button.classList.add('is-added');
        window.setTimeout(() => button.classList.remove('is-added'), 900);

        if (cartSections.length) this.updateCartSections(result.sections, cartSections);

        document.dispatchEvent(
          new CustomEvent('sc:upcart-pot-upsell:added', {
            detail: { variantId, parentLineKey, result },
          })
        );
      } catch (error) {
        if (errorElement) {
          errorElement.textContent = error.message;
          errorElement.hidden = false;
        }
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (spinner) spinner.hidden = true;
      }
    }

    getCartSections() {
      if (this.closest('sc-cart-drawer')) {
        return [
          {
            id: 'ScCartDrawer',
            section: 'sc-cart-drawer',
            selector: '#ScCartDrawer',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '#cart-icon-bubble',
            sourceSelector: '#shopify-section-cart-icon-bubble',
          },
        ];
      }

      if (this.closest('cart-drawer')) {
        return [
          {
            id: 'CartDrawer',
            section: 'cart-drawer',
            selector: '.drawer__inner',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '#cart-icon-bubble',
            sourceSelector: '#shopify-section-cart-icon-bubble',
          },
        ];
      }

      const mainCartItems = document.getElementById('main-cart-items');
      const mainCartFooter = document.getElementById('main-cart-footer');

      if (!mainCartItems || !mainCartFooter) return [];

      return [
        {
          id: 'main-cart-items',
          section: mainCartItems.dataset.id,
          selector: '.js-contents',
        },
        {
          id: 'main-cart-footer',
          section: mainCartFooter.dataset.id,
          selector: '.js-contents',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '#cart-icon-bubble',
          sourceSelector: '#shopify-section-cart-icon-bubble',
        },
      ].filter((section) => section.section);
    }

    updateCartSections(renderedSections, cartSections) {
      if (!renderedSections) return;

      const nativeCartDrawer = this.closest('sc-cart-drawer');

      if (nativeCartDrawer && typeof nativeCartDrawer.updateRenderedSections === 'function') {
        nativeCartDrawer.updateRenderedSections(renderedSections);
        document.dispatchEvent(
          new CustomEvent('cart:changed', { detail: { source: 'sc-cart-drawer' } })
        );
        return;
      }

      cartSections.forEach((section) => {
        const sectionHtml = renderedSections[section.section];

        if (!sectionHtml) return;

        const parsedHtml = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const targetRoot = document.getElementById(section.id);
        const target = section.selector === `#${section.id}` ? targetRoot : targetRoot?.querySelector(section.selector);
        const source = parsedHtml.querySelector(section.sourceSelector || section.selector);

        if (target && source) target.innerHTML = source.innerHTML;
      });

      document.dispatchEvent(new CustomEvent('cart:changed'));
    }
  }

  if (!customElements.get('sc-upcart-pot-upsell')) {
    customElements.define('sc-upcart-pot-upsell', ScUpcartPotUpsell);
  }

  const getUpcartRoot = () => document.getElementById('upCart')?.shadowRoot || document;

  const ensureShadowStyles = (root) => {
    if (!(root instanceof ShadowRoot) || !stylesheetUrl) return;
    if (root.querySelector('[data-sc-upcart-pot-upsell-styles]')) return;

    const stylesheet = document.createElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.href = stylesheetUrl;
    stylesheet.dataset.scUpcartPotUpsellStyles = '';
    root.prepend(stylesheet);
  };

  const getProductPath = (url) => {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (error) {
      return null;
    }
  };

  const getProductContainers = (root, item) => {
    if (item.key && typeof root.getElementById === 'function') {
      const keyedElement = root.getElementById(item.key);

      if (keyedElement) {
        if (keyedElement.matches(productTileSelector) || keyedElement.matches(legacyProductRowSelector)) {
          return [keyedElement];
        }

        const keyedContainer = keyedElement.closest(productTileSelector) || keyedElement.closest(legacyProductRowSelector);

        if (keyedContainer) return [keyedContainer];
      }
    }

    const productPath = getProductPath(item.url || `/products/${item.handle}`);

    if (!productPath) return [];

    return Array.from(root.querySelectorAll(`${productTileSelector}, ${legacyProductRowSelector}`)).filter((container) => {
      const productLink = container.querySelector(productLinkSelector) || container.querySelector('a[href*="/products/"]');

      return productLink && getProductPath(productLink.href) === productPath;
    });
  };

  const getUpsellMarkup = (item) => {
    const cacheKey = item.product_id || item.handle || item.url;

    if (!cacheKey) return Promise.resolve('');

    if (!markupCache.has(cacheKey)) {
      const productUrl = new URL(item.url || `/products/${item.handle}`, window.location.origin);
      productUrl.searchParams.set('view', productView);

      markupCache.set(
        cacheKey,
        fetch(productUrl.toString(), { credentials: 'same-origin' })
          .then((response) => (response.ok ? response.text() : ''))
          .catch(() => '')
      );
    }

    return markupCache.get(cacheKey);
  };

  const createWidget = (markup) => {
    const template = document.createElement('template');
    template.innerHTML = markup.trim();

    return template.content.querySelector(widgetSelector);
  };

  const insertWidget = (root, item, markup, token, attempt = 0) => {
    if (token !== renderToken || !markup.trim()) return;

    const productContainers = getProductContainers(root, item);

    if (!productContainers.length && attempt < 10) {
      window.setTimeout(() => insertWidget(root, item, markup, token, attempt + 1), 100);
      return;
    }

    productContainers.forEach((productContainer) => {
      if (productContainer.dataset.scUpcartPotUpsell === 'true') return;

      const widget = createWidget(markup);

      if (!widget) return;

      widget.dataset.scUpcartPotUpsellInjected = '';
      productContainer.dataset.scUpcartPotUpsell = 'true';
      productContainer.insertAdjacentElement('afterend', widget);
    });
  };

  const cartHasMatchingPot = (items, markup) => {
    const cartHandles = new Set(
      items.filter((item) => !item.parent_relationship).map((item) => item.handle).filter(Boolean)
    );

    if (!cartHandles.size || !markup) return false;

    const template = document.createElement('template');
    template.innerHTML = markup.trim();

    return Array.from(template.content.querySelectorAll('[data-product-url]')).some((element) => {
      const productUrl = element.dataset.productUrl || '';
      const handle = productUrl.split('/products/')[1]?.split(/[?#]/)[0];

      return handle && cartHandles.has(handle);
    });
  };

  const renderUpsells = (items = []) => {
    const root = getUpcartRoot();
    const token = ++renderToken;
    const parentKeysWithChildren = new Set(
      items
        .filter((item) => item.parent_relationship?.parent_key)
        .map((item) => item.parent_relationship.parent_key)
    );

    ensureShadowStyles(root);
    root.querySelectorAll(injectedWidgetSelector).forEach((widget) => widget.remove());
    root.querySelectorAll('[data-sc-upcart-pot-upsell="true"]').forEach((container) => {
      delete container.dataset.scUpcartPotUpsell;
    });

    items.forEach((item) => {
      if (item.parent_relationship) return;
      if (parentKeysWithChildren.has(item.key)) return;

      getUpsellMarkup(item).then((markup) => {
        if (token !== renderToken || !markup.trim()) return;
        if (cartHasMatchingPot(items, markup)) return;

        insertWidget(root, item, markup, token);
      });
    });
  };

  const scheduleRender = (items) => {
    window.requestAnimationFrame(() => renderUpsells(items));
  };

  const subscribeToUpcart = (attempt = 0) => {
    if (window.scUpcartPotUpsellSubscribed) return;
    if (window.scCartExperiment?.scIsNative?.()) return;

    if (
      typeof window.upcartSubscribeCartLoaded !== 'function' ||
      typeof window.upcartSubscribeCartUpdated !== 'function'
    ) {
      if (attempt < maxSubscriptionAttempts) {
        window.setTimeout(() => subscribeToUpcart(attempt + 1), 50);
      }
      return;
    }

    window.scUpcartPotUpsellSubscribed = true;
    window.upcartSubscribeCartLoaded(({ cart }) => scheduleRender(cart?.items || []));
    window.upcartSubscribeCartUpdated(({ cart }) => scheduleRender(cart?.items || []));

    const cart = window.upcartGetCart?.();
    if (cart?.items) scheduleRender(cart.items);
  };

  document.addEventListener('sc:cart-variant-change', (event) => {
    if (event.detail?.variant === 'upcart') subscribeToUpcart();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', subscribeToUpcart, { once: true });
  } else {
    subscribeToUpcart();
  }
})();
